import { BLOWS, type Ability, type Blow, type Rules, type Weapon } from "./types.ts";

/**
 * The weapon link (ADR-069 decision 4): a character performs the blows of the weapon it holds, so a blow's name,
 * strip and numbers are those of the ability its weapon names for it. Until equipment is state, the weapon every
 * character holds is the rules' starting weapon.
 */

/**
 * The ability a weapon's blow performs.
 *
 * @param rules The rules.
 * @param weapon A weapon these rules hold.
 * @param blow The blow: `basic`, a left click toward the pointer, or `heavy`, the heavy blow.
 * @returns The ability, or null when the weapon has no such blow.
 * @throws RangeError when the weapon is not one these rules hold, a caller's mistake.
 * @example
 * blowOf(rules, rules.startingWeapon, "basic")?.name;
 * // "Strike"
 */
export function blowOf(rules: Rules, weapon: Weapon, blow: Blow): Ability | null {
  if (rules.weapons.get(weapon.id) !== weapon) {
    throw new RangeError(`the weapon ${weapon.id} is not one these rules hold`);
  }
  const named = weapon.blows.find((entry) => entry.blow === blow);
  if (named === undefined) {
    return null;
  }
  const ability = rules.abilities.get(named.ability);
  if (ability === undefined) {
    throw new RangeError(`the ${blow} blow of ${weapon.id} names ${named.ability}, which these rules do not hold`);
  }
  return ability;
}

/**
 * The bar every character holds (ADR-073 decision 5, item 3, and its amendment of 2026-10-02, item 2): the starting
 * weapon's blows in the order of `BLOWS`, basic first, then the abilities every character is given in the order the
 * abilities table holds them, which the map server accepts acts for and the page's ability bar draws in its slots.
 *
 * @param rules The rules.
 * @returns The abilities, in slot order.
 * @example
 * barOf(loadRules()).map((ability) => ability.name);
 * // ["Strike", "Heavy Strike", "Evasion"]
 */
export function barOf(rules: Rules): readonly Ability[] {
  const bar: Ability[] = [];
  for (const blow of BLOWS) {
    const ability = blowOf(rules, rules.startingWeapon, blow);
    if (ability !== null) {
      bar.push(ability);
    }
  }
  for (const ability of rules.tables.abilities) {
    if (ability.given) {
      bar.push(ability);
    }
  }
  return bar;
}
